// src/api/services/groupService.ts
import apiClient from '@/api/apiClient';
import { AxiosResponse } from 'axios';

export interface Group {
    id: number;
    code: string;
    name: string;
    description: string;
    registrationType: 'self_registration' | 'approval_required' | 'admin_only';
    order: number;
    roleFunctionCodes?: string[];
}

export interface StrapiResponse<T> {
    data: {
        id: number;
        attributes: Omit<T, 'id'> & {
            roleFunctions?: {
                data: Array<{
                    id: number;
                    attributes: {
                        code: string;
                        name: string;
                        description: string;
                    }
                }>
            }
        }
    }[];
    meta: {
        pagination: {
            page: number;
            pageSize: number;
            pageCount: number;
            total: number;
        }
    }
}

/**
 * Service quản lý các thao tác liên quan đến Group
 */
class GroupService {
    /**
     * Lấy danh sách các nhóm có thể đăng ký
     */
    async getRegisterableGroups(requireAuth = false): Promise<Group[]> {
        try {


            const response: AxiosResponse<StrapiResponse<Group>> = await apiClient.get(
                '/api/groups/registerable',

            );

            // Chuyển đổi dữ liệu từ cấu trúc Strapi sang cấu trúc Group
            const groups = response.data.data.map(item => ({
                id: item.id,
                code: item.code,
                name: item.name,
                description: item.description,
                registrationType: item.registrationType as 'self_registration' | 'approval_required' | 'admin_only',
                order: item.order,
                roleFunctionCodes: item.roleFunctions?.data?.map(rf => rf.code) || []
            }));

            // Sắp xếp theo thứ tự
            return groups.sort((a, b) => a.order - b.order);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Lấy danh sách tất cả các nhóm
     */
    async getAllGroups(): Promise<Group[]> {
        try {
            const response: AxiosResponse<StrapiResponse<Group>> = await apiClient.get('/api/groups?populate=roleFunctions');

            const groups = response.data.data.map(item => ({
                id: item.id,
                code: item.code,
                name: item.name,
                description: item.description,
                registrationType: item.registrationType as 'self_registration' | 'approval_required' | 'admin_only',
                order: item.order,
                roleFunctionCodes: item.roleFunctions?.data?.map(rf => rf.code) || []
            }));

            return groups;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Lấy thông tin chi tiết của một nhóm
     */
    async getGroupByCode(code: string): Promise<Group | null> {
        try {
            const response: AxiosResponse<StrapiResponse<Group>> = await apiClient.get(`/api/groups?filters[code][$eq]=${code}&populate=roleFunctions`);

            if (response.data.data.length === 0) {
                return null;
            }

            const item = response.data.data[0];
            return {
                id: item.id,
                code: item.code,
                name: item.name,
                description: item.description,
                registrationType: item.registrationType as 'self_registration' | 'approval_required' | 'admin_only',
                order: item.order,
                roleFunctionCodes: item.roleFunctions?.data?.map(rf => rf.code) || []
            };
        } catch (error) {
            throw error;
        }
    }
}

export default new GroupService();